import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from './Logo';
import { useContentVertex } from '../contexts/ContentVertexContext';
import {
    NavigationMenu,
    NavigationMenuContent,
    NavigationMenuItem,
    NavigationMenuList,
    NavigationMenuTrigger,
} from './ui/navigation-menu';
import {
    DashboardIcon,
    LightningBoltIcon,
    GlobeIcon,
    MagnifyingGlassIcon,
    RocketIcon,
    TriangleUpIcon,
    StarIcon,
    PlusIcon,
    HamburgerMenuIcon,
    Cross2Icon,
    ExitIcon,
    MixerHorizontalIcon,
    ActivityLogIcon,
    ReaderIcon,
    TargetIcon,
} from '@radix-ui/react-icons';

// ── Data ─────────────────────────────────────────────────────────────────────

const DIRECT_TABS = [
    { icon: DashboardIcon,     label: 'Dashboard',       path: '/',               badgeKey: 'streak' },
    { icon: LightningBoltIcon, label: 'Topic Queue',     path: '/topic-queue',    badgeKey: 'pendingCount' },
    { icon: GlobeIcon,         label: 'WordPress Sites', path: '/wordpress-sites' },
];

const KEYWORD_RESEARCH = {
    id: 'research',
    label: 'Keyword Research',
    // left col — main features
    features: [
        { icon: ActivityLogIcon,      label: 'Intelligence',  description: 'Trend analysis, seasonality and timing recommendations',  path: '/keyword-intelligence' },
        { icon: MixerHorizontalIcon,  label: 'Comparison',    description: 'Compare up to 5 keywords side-by-side',                  path: '/keyword-comparison' },
        { icon: GlobeIcon,            label: 'Geographic',    description: 'Where keywords are most popular — target right audience', path: '/geographic-optimizer' },
        { icon: ReaderIcon,           label: 'Multilanguage', description: 'Find trending topics in other countries first',           path: '/multilanguage-trends' },
    ],
    // right col — promo card
    promo: {
        title: 'Keyword Hub',
        description: 'All keyword research tools in one place. Find, compare and optimise for the keywords that matter.',
        cta: 'Open Intelligence',
        path: '/keyword-intelligence',
    },
};

const TRENDS = {
    id: 'trends',
    label: 'Trends',
    groups: [
        {
            label: 'Discovery',
            items: [
                { icon: RocketIcon,  label: 'Trend Discovery',    description: 'Find trending topics with growth % and difficulty', path: '/trend-discovery' },
            ],
        },
        {
            label: 'Competitive',
            items: [
                { icon: TargetIcon, label: 'Competitor Analysis', description: 'Discover what trending topics competitors miss',   path: '/competitor-analysis' },
            ],
        },
    ],
    promo: {
        title: 'Trends',
        description: 'Discover what\'s trending, find content gaps, and stay ahead of your competitors.',
        cta: 'Trend Discovery',
        path: '/trend-discovery',
    },
};

const MEASURE = {
    id: 'measure',
    label: 'Measure',
    features: [
        { icon: TriangleUpIcon, label: 'Rank Above',          description: 'Analyse competitors and find content to outrank them', path: '/rank-above' },
        { icon: StarIcon,       label: 'AI Search Optimizer', description: 'Optimise for AI search engines and LLM visibility',   path: '/ai-search-optimizer' },
    ],
    promo: {
        title: 'Measure Performance',
        description: 'Track your rankings and optimise for AI search engines and LLM visibility.',
        cta: 'AI Search Optimizer',
        path: '/ai-search-optimizer',
    },
};

// ── Sub-components ────────────────────────────────────────────────────────────

const FeatureLink = ({ item, onNavigate }) => {
    const Icon = item.icon;
    return (
        <button
            onClick={() => onNavigate(item.path)}
            className="flex items-start gap-3 rounded-lg p-2 hover:bg-white/[0.06] transition-colors text-left w-full group"
        >
            <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-md bg-white/[0.06] text-zinc-400 group-hover:text-zinc-200 flex-shrink-0">
                <Icon className="h-4 w-4" />
            </div>
            <div className="min-w-0">
                <p className="text-sm font-medium text-zinc-200 group-hover:text-white leading-tight">{item.label}</p>
                <p className="text-xs text-zinc-500 group-hover:text-zinc-400 mt-0.5 leading-snug">{item.description}</p>
            </div>
        </button>
    );
};

const PromoCard = ({ promo, onNavigate }) => (
    <div className="rounded-xl bg-gradient-to-br from-violet-500/10 to-blue-500/10 border border-white/[0.08] p-4 flex flex-col gap-3">
        <div>
            <h4 className="font-semibold text-zinc-100 text-sm">{promo.title}</h4>
            <p className="text-xs text-zinc-400 mt-1 leading-relaxed">{promo.description}</p>
        </div>
        <button
            onClick={() => onNavigate(promo.path)}
            className="w-full text-center text-xs font-semibold px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white transition-colors"
        >
            {promo.cta}
        </button>
    </div>
);

// ── Main TopNav ───────────────────────────────────────────────────────────────

const TopNav = ({ user }) => {
    const { pendingCount, onGenerateClick, currentStreak, streakAtRisk, creditBalance, creditLimit } = useContentVertex();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);
    const [creditsExhausted, setCreditsExhausted] = useState(false);

    useEffect(() => {
        setMobileOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        const handler = () => setCreditsExhausted(true);
        window.addEventListener('credits-exhausted', handler);
        return () => window.removeEventListener('credits-exhausted', handler);
    }, []);

    const handleNavigate = (path) => {
        setMobileOpen(false);
        navigate(path);
    };

    const handleLogout = () => {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        window.location.href = '/';
    };

    const isDirectActive = (path) =>
        path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

    return (
        <>
            <header className="fixed top-0 left-0 right-0 z-40 bg-zinc-950/90 backdrop-blur-xl border-b border-white/[0.07]">
                <div className="max-w-screen-2xl mx-auto px-6 h-14 flex items-center gap-3">

                    {/* Logo */}
                    <button onClick={() => handleNavigate('/')} className="flex items-center gap-1.5 flex-shrink-0 mr-1">
                        <Logo className="h-7 w-7" />
                        <span className="text-base font-bold bg-gradient-to-r from-blue-400 to-violet-400 bg-clip-text text-transparent">
                            ContentVertex
                        </span>
                    </button>

                    <div className="hidden md:block h-5 w-px bg-white/[0.08] flex-shrink-0" />

                    {/* Desktop nav */}
                    <div className="hidden md:flex items-center flex-1">

                        {/* Direct tabs */}
                        {DIRECT_TABS.map((tab) => {
                            const Icon = tab.icon;
                            const isActive = isDirectActive(tab.path);
                            const badge = tab.badgeKey === 'pendingCount' ? pendingCount : null;
                            return (
                                <button
                                    key={tab.path}
                                    onClick={() => handleNavigate(tab.path)}
                                    className={`relative flex items-center gap-1.5 px-3 h-14 text-sm font-medium transition-all duration-150 border-b-2 whitespace-nowrap
                                        ${isActive
                                            ? 'text-zinc-100 border-violet-400'
                                            : 'text-zinc-400 hover:text-zinc-200 border-transparent'
                                        }`}
                                >
                                    {tab.label}
                                    {badge != null && badge > 0 && (
                                        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-amber-500/20 border border-amber-500/40 text-[9px] font-bold text-amber-400">
                                            {badge > 9 ? '9+' : badge}
                                        </span>
                                    )}
                                    {tab.path === '/' && currentStreak > 0 && (
                                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${streakAtRisk ? 'bg-amber-500/20 text-amber-400 animate-pulse-slow' : 'bg-zinc-800 text-zinc-500'}`}>
                                            🔥{currentStreak}
                                        </span>
                                    )}
                                </button>
                            );
                        })}

                        {/* Shadcn NavigationMenu for dropdowns */}
                        <NavigationMenu className="ml-0">
                            <NavigationMenuList className="gap-0">

                                {/* ── Keyword Research ── */}
                                <NavigationMenuItem>
                                    <NavigationMenuTrigger className="h-14 px-3 text-sm rounded-none border-b-2 border-transparent data-[state=open]:border-blue-400 data-[state=open]:text-zinc-100 text-zinc-400 hover:text-zinc-200 bg-transparent hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent">
                                        {KEYWORD_RESEARCH.label}
                                    </NavigationMenuTrigger>
                                    <NavigationMenuContent>
                                        <div className="w-[640px] p-5">
                                            <div className="grid grid-cols-[1fr_200px] gap-6">
                                                {/* Left — features */}
                                                <div>
                                                    <p className="text-[10px] font-bold uppercase tracking-widest text-blue-400 mb-3">Keyword Tools</p>
                                                    <div className="space-y-1">
                                                        {KEYWORD_RESEARCH.features.map((item) => (
                                                            <FeatureLink key={item.path} item={item} onNavigate={handleNavigate} />
                                                        ))}
                                                    </div>
                                                </div>
                                                {/* Right — promo */}
                                                <PromoCard promo={KEYWORD_RESEARCH.promo} onNavigate={handleNavigate} />
                                            </div>
                                        </div>
                                    </NavigationMenuContent>
                                </NavigationMenuItem>

                                {/* ── Trends ── */}
                                <NavigationMenuItem>
                                    <NavigationMenuTrigger className="h-14 px-3 text-sm rounded-none border-b-2 border-transparent data-[state=open]:border-violet-400 data-[state=open]:text-zinc-100 text-zinc-400 hover:text-zinc-200 bg-transparent hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent">
                                        {TRENDS.label}
                                    </NavigationMenuTrigger>
                                    <NavigationMenuContent>
                                        <div className="w-[580px] p-5">
                                            <div className="grid grid-cols-[1fr_200px] gap-6">
                                                {/* Left — grouped */}
                                                <div className="space-y-4">
                                                    {TRENDS.groups.map((group) => (
                                                        <div key={group.label}>
                                                            <p className="text-[10px] font-bold uppercase tracking-widest text-violet-400 mb-2">{group.label}</p>
                                                            <div className="space-y-1">
                                                                {group.items.map((item) => (
                                                                    <FeatureLink key={item.path} item={item} onNavigate={handleNavigate} />
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                                {/* Right — promo */}
                                                <PromoCard promo={TRENDS.promo} onNavigate={handleNavigate} />
                                            </div>
                                        </div>
                                    </NavigationMenuContent>
                                </NavigationMenuItem>

                                {/* ── Measure ── */}
                                <NavigationMenuItem>
                                    <NavigationMenuTrigger className="h-14 px-3 text-sm rounded-none border-b-2 border-transparent data-[state=open]:border-emerald-400 data-[state=open]:text-zinc-100 text-zinc-400 hover:text-zinc-200 bg-transparent hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent">
                                        {MEASURE.label}
                                    </NavigationMenuTrigger>
                                    <NavigationMenuContent>
                                        <div className="w-[540px] p-5">
                                            <div className="grid grid-cols-[1fr_200px] gap-6">
                                                {/* Left — features */}
                                                <div>
                                                    <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-400 mb-3">Tools</p>
                                                    <div className="space-y-1">
                                                        {MEASURE.features.map((item) => (
                                                            <FeatureLink key={item.path} item={item} onNavigate={handleNavigate} />
                                                        ))}
                                                    </div>
                                                </div>
                                                {/* Right — promo */}
                                                <PromoCard promo={MEASURE.promo} onNavigate={handleNavigate} />
                                            </div>
                                        </div>
                                    </NavigationMenuContent>
                                </NavigationMenuItem>

                            </NavigationMenuList>
                        </NavigationMenu>
                    </div>

                    {/* Right side — desktop */}
                    <div className="hidden md:flex items-center gap-3 ml-auto">
                        {/* Credit balance pill */}
                        {creditBalance !== null && (
                            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.05] border border-white/[0.08] text-xs select-none">
                                <span className="text-yellow-400">⚡</span>
                                <span className="text-zinc-200 font-medium">{creditBalance}</span>
                                <span className="text-zinc-600">/ {creditLimit}</span>
                            </div>
                        )}
                        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg hover:bg-white/[0.05] transition-all cursor-pointer">
                            <div className="h-6 w-6 rounded-full bg-gradient-to-br from-blue-500 to-violet-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                {user?.name?.charAt(0) || 'U'}
                            </div>
                            <span className="text-sm text-zinc-300 max-w-[100px] truncate">{user?.name || 'User'}</span>
                        </div>
                        <button
                            onClick={handleLogout}
                            className="p-2 rounded-lg text-zinc-600 hover:text-zinc-300 hover:bg-white/[0.06] transition-all"
                            title="Logout"
                        >
                            <ExitIcon className="h-4 w-4" />
                        </button>
                        <button
                            onClick={onGenerateClick}
                            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500 text-white font-semibold text-sm shadow-lg shadow-violet-500/20 transition-all duration-200 hover:scale-[1.02] active:scale-95 flex-shrink-0"
                        >
                            <PlusIcon className="h-3.5 w-3.5" />
                            Generate
                        </button>
                    </div>

                    {/* Mobile hamburger */}
                    <button
                        className="md:hidden ml-auto p-2 rounded-lg text-zinc-400 hover:text-zinc-100 hover:bg-white/[0.06] transition-all"
                        onClick={() => setMobileOpen((v) => !v)}
                    >
                        {mobileOpen ? <Cross2Icon className="h-5 w-5" /> : <HamburgerMenuIcon className="h-5 w-5" />}
                    </button>
                </div>
            </header>

            {/* ── Mobile drawer ─────────────────────────────────────────────── */}
            {mobileOpen && (
                <div className="fixed inset-0 z-50 flex flex-col bg-zinc-950/98 backdrop-blur-xl pt-14 overflow-y-auto">
                    <div className="px-5 py-4 space-y-5">

                        {/* Direct tabs */}
                        <div className="space-y-1">
                            {DIRECT_TABS.map((tab) => {
                                const Icon = tab.icon;
                                const isActive = isDirectActive(tab.path);
                                const badge = tab.badgeKey === 'pendingCount' ? pendingCount : null;
                                return (
                                    <button
                                        key={tab.path}
                                        onClick={() => handleNavigate(tab.path)}
                                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-left text-sm font-medium ${
                                            isActive ? 'bg-violet-500/10 text-zinc-100' : 'text-zinc-400 hover:bg-white/[0.05] hover:text-zinc-200'
                                        }`}
                                    >
                                        <Icon className="h-4 w-4 flex-shrink-0" />
                                        {tab.label}
                                        {badge != null && badge > 0 && (
                                            <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
                                                {badge > 9 ? '9+' : badge}
                                            </span>
                                        )}
                                        {tab.path === '/' && currentStreak > 0 && (
                                            <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-zinc-800 text-zinc-500">
                                                🔥{currentStreak}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>

                        <div className="h-px bg-white/[0.06]" />

                        {/* Keyword Research */}
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-widest text-blue-400 px-3 mb-2">Keyword Research</p>
                            <div className="space-y-1">
                                {KEYWORD_RESEARCH.features.map((item) => {
                                    const Icon = item.icon;
                                    return (
                                        <button key={item.path} onClick={() => handleNavigate(item.path)}
                                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-zinc-400 hover:bg-white/[0.05] hover:text-zinc-200 transition-all text-left text-sm font-medium">
                                            <Icon className="h-4 w-4 flex-shrink-0" />{item.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="h-px bg-white/[0.06]" />

                        {/* Trends */}
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-widest text-violet-400 px-3 mb-2">Trends</p>
                            <div className="space-y-1">
                                {TRENDS.groups.flatMap((g) => g.items).map((item) => {
                                    const Icon = item.icon;
                                    return (
                                        <button key={item.path} onClick={() => handleNavigate(item.path)}
                                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-zinc-400 hover:bg-white/[0.05] hover:text-zinc-200 transition-all text-left text-sm font-medium">
                                            <Icon className="h-4 w-4 flex-shrink-0" />{item.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="h-px bg-white/[0.06]" />

                        {/* Measure */}
                        <div>
                            <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-400 px-3 mb-2">Measure</p>
                            <div className="space-y-1">
                                {MEASURE.features.map((item) => {
                                    const Icon = item.icon;
                                    return (
                                        <button key={item.path} onClick={() => handleNavigate(item.path)}
                                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-zinc-400 hover:bg-white/[0.05] hover:text-zinc-200 transition-all text-left text-sm font-medium">
                                            <Icon className="h-4 w-4 flex-shrink-0" />{item.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="h-px bg-white/[0.06]" />

                        {/* Bottom actions */}
                        <div className="space-y-2 pb-4">
                            <button onClick={onGenerateClick}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-blue-600 text-white font-semibold text-sm">
                                <PlusIcon className="h-4 w-4" />Generate Article
                            </button>
                            <button onClick={handleLogout}
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.06] text-zinc-400 text-sm font-medium">
                                <ExitIcon className="h-4 w-4" />Logout
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Spacer */}
            <div className="h-14 flex-shrink-0" />

            {/* Insufficient Credits Modal */}
            {creditsExhausted && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setCreditsExhausted(false)} />
                    <div className="relative w-full max-w-sm rounded-2xl bg-zinc-900 border border-white/[0.08] shadow-2xl p-6 flex flex-col gap-4">
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-yellow-500/10 border border-yellow-500/20 flex items-center justify-center flex-shrink-0">
                                <span className="text-lg">⚡</span>
                            </div>
                            <div>
                                <h3 className="text-base font-semibold text-zinc-100">Out of credits</h3>
                                <p className="text-xs text-zinc-500 mt-0.5">You've used all credits for this billing period.</p>
                            </div>
                        </div>
                        <p className="text-sm text-zinc-400 leading-relaxed">
                            Credits reset monthly. Upgrade your plan to get more credits and keep using keyword research, trends, and article generation.
                        </p>
                        <div className="flex flex-col gap-2">
                            <button
                                onClick={() => { setCreditsExhausted(false); navigate('/settings'); }}
                                className="w-full py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500 text-white font-semibold text-sm transition-all"
                            >
                                Upgrade Plan
                            </button>
                            <button
                                onClick={() => setCreditsExhausted(false)}
                                className="w-full py-2 rounded-xl text-zinc-500 hover:text-zinc-300 text-sm transition-colors"
                            >
                                Dismiss
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default TopNav;
